import { useState } from 'react';
import { ChevronDown, MoreHorizontal, Plus, Search, Shuffle } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Drawer } from '../../components/Drawer';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { useCoupons } from '../../api/catalog';

/** Coupons — table + new-coupon drawer. */
export function Coupons() {
  const { data: coupons = [], isLoading } = useCoupons();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = coupons.filter((c) => !search || c.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Pricing"
        title="Coupons"
        subtitle="Promo codes for percent / fixed / trial-extension / free-intervals."
        action={<Button iconLeft={<Plus className="w-4 h-4" />} onClick={() => setDrawerOpen(true)}>New coupon</Button>}
      />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <Filter label="Status" value="All" />
          <Filter label="Owner" value="Platform / All" />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search code…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Code</th>
                <th className="text-left font-medium px-4 py-3">Type</th>
                <th className="text-left font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">Redeemed</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Expires</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-24 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-32 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-12 rounded" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    {coupons.length === 0 ? 'No coupons yet.' : 'No coupons match your search.'}
                  </td>
                </tr>
              ) : (
                filtered.map((c) => (
                <tr key={c.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <div className="font-medium text-zinc-950 dark:text-zinc-50 mono">{c.code}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400">{c.internalName}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 capitalize">{c.discountType.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{c.amountLabel}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums">
                    {c.redemptionsUsed} / {c.redemptionsLimit ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell tabular-nums">
                    {c.expiresAt ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={c.status === 'active' ? 'success' : c.status === 'expired' ? 'warning' : 'neutral'}>
                      {c.status.charAt(0).toUpperCase() + c.status.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <button className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <NewCouponDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
}

function Filter({ label, value }: { label: string; value: string }) {
  return (
    <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition">
      <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
      <span>{value}</span>
      <ChevronDown className="w-3 h-3 text-zinc-400" />
    </button>
  );
}

function NewCouponDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [type, setType] = useState<'percent' | 'fixed' | 'trial' | 'free_intervals'>('percent');
  const [duration, setDuration] = useState<'once' | 'intervals' | 'forever'>('once');
  const [audience, setAudience] = useState<'new' | 'existing' | 'both'>('new');
  const [code, setCode] = useState('EID2026');

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="New coupon"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onClose}>Save</Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
          <Input label="Code" required value={code} onChange={(e) => setCode(e.target.value)} />
          <Button variant="secondary" iconLeft={<Shuffle className="w-3.5 h-3.5" />} onClick={() => setCode('PROMO-' + Math.random().toString(36).slice(2, 7).toUpperCase())}>
            Random
          </Button>
        </div>
        <Input label="Internal name" defaultValue="Eid 2026 promo" />

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Discount type</span>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: 'percent', l: 'Percent off' },
              { v: 'fixed', l: 'Fixed amount off' },
              { v: 'trial', l: 'Trial extension' },
              { v: 'free_intervals', l: 'Free first N intervals' },
            ].map((t) => (
              <label key={t.v} className={cn(
                'flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md border transition',
                type === t.v
                  ? 'border-brand bg-brand-subtle dark:border-brand-dm dark:bg-brand-dm-subtle/30'
                  : 'border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
              )}>
                <input type="radio" name="type" checked={type === t.v} onChange={() => setType(t.v as typeof type)} className="text-brand focus:ring-brand" />
                {t.l}
              </label>
            ))}
          </div>
        </div>

        {(type === 'percent' || type === 'fixed') && (
          <Input label="Amount" required defaultValue="20" suffix={<span className="text-xs">{type === 'percent' ? '%' : '৳'}</span>} />
        )}

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Duration</span>
          <div className="space-y-2">
            <Radio name="duration" checked={duration === 'once'} onChange={() => setDuration('once')} label="Once (next invoice only)" />
            <Radio name="duration" checked={duration === 'intervals'} onChange={() => setDuration('intervals')} label="N intervals (e.g., 3 months)" />
            <Radio name="duration" checked={duration === 'forever'} onChange={() => setDuration('forever')} label="Forever (recurring)" />
          </div>
          {duration === 'intervals' && (
            <div className="mt-2 max-w-[140px]">
              <Input defaultValue="3" />
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Restrictions</h3>
          <div className="space-y-3">
            <Input label="Min subscription value (BDT)" placeholder="—" />
            <Input label="Max redemptions" defaultValue="200" hint="Total across all customers" />
            <Input label="Per-customer limit" defaultValue="1" hint="One redemption per business" />
          </div>
        </div>

        <div>
          <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">Eligible audience</span>
          <div className="space-y-2">
            <Radio name="aud" checked={audience === 'new'} onChange={() => setAudience('new')} label="New customers only" />
            <Radio name="aud" checked={audience === 'existing'} onChange={() => setAudience('existing')} label="Existing customers only" />
            <Radio name="aud" checked={audience === 'both'} onChange={() => setAudience('both')} label="Both" />
          </div>
        </div>

        <div className="pt-3 border-t border-zinc-200 dark:border-zinc-700">
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-3">Validity</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input label="Active from" type="date" defaultValue="2026-05-01" />
            <Input label="Expires" type="date" defaultValue="2026-05-30" />
          </div>
        </div>
      </div>
    </Drawer>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input type="radio" name={name} checked={checked} onChange={onChange} className="text-brand focus:ring-brand" />
      {label}
    </label>
  );
}
